import {
  Bell,
  BellOff,
  CheckCircle2,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useDND } from "../../contexts/DNDContext";
import { useServerNotifications } from "../../contexts/NotificationsContext";
import { useBackend } from "../../lib/backend";
import type { DeskInboxItem } from "../../lib/services/desk-inbox";
import { useActiveDesk } from "../../hooks/useActiveDesk";
import {
  DeskActionNotificationRow,
  QueuedNotificationRow,
  ServerNotificationRow,
} from "./BulletinInboxRows";
import { FintheonNumericText } from "../numbers/FintheonNumericText";
import { FadingDivider } from "../shared/FadingDivider";
import "../notifications/s156-cardless-notifications.css";

export function BulletinInboxTab() {
  const backend = useBackend();
  const { storageId } = useActiveDesk();
  const { queue, dismissQueued, clearQueue, dndActive, toggleManualDnd } =
    useDND();
  const {
    notifications,
    unreadCount,
    loading,
    refresh,
    markRead,
    markAllRead,
    clearOne,
    clearAll,
  } = useServerNotifications();
  const [deskItems, setDeskItems] = useState<DeskInboxItem[]>([]);
  const [activeDeskItemId, setActiveDeskItemId] = useState<string | null>(null);

  const loadDeskItems = useCallback(async () => {
    try {
      const items = await backend.deskInbox.list(storageId);
      setDeskItems(
        items.filter(
          (item) =>
            item.status === "pending" &&
            (item.type === "desk_join_request" ||
              item.type === "regime_change"),
        ),
      );
    } catch {
      setDeskItems([]);
    }
  }, [backend, storageId]);

  useEffect(() => {
    void loadDeskItems();
  }, [loadDeskItems]);

  const totalCount = notifications.length + queue.length + deskItems.length;
  const canClear = totalCount > 0;
  const canMarkRead = unreadCount > 0;

  const handleClearAll = async () => {
    clearQueue();
    if (notifications.length > 0) await clearAll();
  };

  const decideDeskItem = async (
    item: DeskInboxItem,
    action: "approve" | "dismiss",
  ) => {
    setActiveDeskItemId(item.id);
    try {
      if (item.type === "regime_change" && action === "approve")
        await backend.deskInbox.approveRegimeProposalForItem(item);
      if (item.type === "regime_change" && action === "dismiss")
        await backend.deskInbox.denyRegimeProposalForItem(item);
      if (action === "approve")
        await backend.deskInbox.approve(item.id, storageId);
      if (action === "dismiss")
        await backend.deskInbox.dismiss(item.id, storageId);
      await loadDeskItems();
    } finally {
      setActiveDeskItemId(null);
    }
  };

  return (
    <div className="space-y-3" data-s156-notification-inbox>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Bell
              aria-hidden="true"
              className="h-3.5 w-3.5 text-[var(--fintheon-accent)]"
            />
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--fintheon-accent)]">
              Inbox
            </span>
            {unreadCount > 0 ? (
              <span
                className="text-[9px] font-semibold text-[var(--fintheon-accent)]"
                aria-label={`${unreadCount} unread notifications`}
              >
                (
                <FintheonNumericText
                  text={unreadCount > 9 ? "9+" : unreadCount}
                  kind="count"
                />
                )
              </span>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          onClick={toggleManualDnd}
          className={`s156-bulletin-icon-control shrink-0 ${
            dndActive
              ? "text-[var(--fintheon-accent)]"
              : "text-[var(--fintheon-muted)]"
          }`}
          title={dndActive ? "Disable DND" : "Enable DND"}
          aria-label={
            dndActive ? "Disable Do Not Disturb" : "Enable Do Not Disturb"
          }
          aria-pressed={dndActive}
        >
          {dndActive ? (
            <BellOff aria-hidden="true" className="h-3.5 w-3.5" />
          ) : (
            <Bell aria-hidden="true" className="h-3.5 w-3.5" />
          )}
        </button>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void refresh()}
          className="s156-bulletin-icon-control"
          title={loading ? "Syncing notifications" : "Sync notifications"}
          aria-label={loading ? "Syncing notifications" : "Sync notifications"}
        >
          <RefreshCw
            aria-hidden="true"
            className={`h-3.5 w-3.5 ${
              loading ? "animate-spin motion-reduce:animate-none" : ""
            }`}
          />
        </button>
        <button
          type="button"
          disabled={!canMarkRead}
          onClick={() => void markAllRead()}
          className="s156-bulletin-icon-control disabled:opacity-35"
          title="Mark all notifications read"
          aria-label="Mark all notifications read"
        >
          <CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          disabled={!canClear}
          onClick={() => void handleClearAll()}
          className="s156-bulletin-icon-control ml-auto text-red-300/70 disabled:opacity-35"
          title="Clear all notifications"
          aria-label="Clear all notifications"
        >
          <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
        </button>
      </div>

      {totalCount === 0 ? (
        <div className="grid min-h-[136px] place-items-center px-4 py-8 text-center">
          <div>
            <Bell
              aria-hidden="true"
              className="mx-auto mb-2 h-6 w-6 text-[var(--fintheon-muted)]/40"
            />
            <p className="text-[11px] text-[var(--fintheon-muted)]">
              No notifications
            </p>
          </div>
        </div>
      ) : (
        <div>
          {notifications.map((notification, index) => (
            <Fragment key={notification.id}>
              <ServerNotificationRow
                notification={notification}
                onRead={markRead}
                onClear={clearOne}
              />
              {index < totalCount - 1 ? (
                <FadingDivider
                  inset={12}
                  className="s156-notification-divider"
                />
              ) : null}
            </Fragment>
          ))}
          {deskItems.map((item, index) => {
            const rowIndex = notifications.length + index;
            return (
              <Fragment key={item.id}>
                <DeskActionNotificationRow
                  item={item}
                  isBusy={activeDeskItemId === item.id}
                  onApprove={() => void decideDeskItem(item, "approve")}
                  onDeny={() => void decideDeskItem(item, "dismiss")}
                />
                {rowIndex < totalCount - 1 ? (
                  <FadingDivider
                    inset={12}
                    className="s156-notification-divider"
                  />
                ) : null}
              </Fragment>
            );
          })}
          {queue.map((notification, index) => {
            const rowIndex = notifications.length + deskItems.length + index;
            return (
              <Fragment key={notification.id}>
                <QueuedNotificationRow
                  notification={notification}
                  onDismiss={dismissQueued}
                />
                {rowIndex < totalCount - 1 ? (
                  <FadingDivider
                    inset={12}
                    className="s156-notification-divider"
                  />
                ) : null}
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
